/**
 * The small pieces the vulnerability list is built from: a record's links,
 * its severity pill, and the two ways a fix is said (one record's fixed
 * version, or the update that closes a whole software's records).
 *
 * The Wordfence record link is not decoration: the feed's licence requires it
 * wherever a record is shown. Links are https only (the server keeps nothing
 * else, and this checks again).
 */
import { ExternalLink } from "lucide-react";
import { cvssText, levelTone, type SecurityVulnView } from "../../../../lib/monitoringSecurity";
import type { VulnGroup } from "../../../../lib/monitoringVulnGroups";
import { StatusPill } from "../../../ui";
import type { T } from "../words";

function SafeLink({ href, children }: { href: string | null; children: string }) {
  if (!href || !href.startsWith("https://")) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline">
      {children}
      <ExternalLink className="size-3" strokeWidth={1.8} />
    </a>
  );
}

/** The severity pill: the score and the rating in the reader's language, else the level. */
export function Severity({ v, t }: { v: SecurityVulnView; t: T }) {
  return (
    <StatusPill tone={levelTone(v.level)} className="whitespace-nowrap">
      {cvssText(v, (rating) => t(`monitoring.security.cvss.${rating}`)) ?? t(`monitoring.security.level.${v.level}`)}
    </StatusPill>
  );
}

/** "possible" (unless the group row already says it), the Wordfence record and the CVE. */
export function Links({ v, t, withPossible = true }: { v: SecurityVulnView; t: T; withPossible?: boolean }) {
  return (
    <span className="mt-1 flex flex-wrap items-center gap-2">
      {withPossible && v.possible ? <StatusPill tone="mut">{t("monitoring.security.possible")}</StatusPill> : null}
      <SafeLink href={v.link}>{t("monitoring.security.record")}</SafeLink>
      {v.cve ? <SafeLink href={v.cveLink}>{v.cve}</SafeLink> : null}
    </span>
  );
}

/** One record's fix: the version it was fixed in, or that there is none yet. */
export function RecordFix({ v, t }: { v: SecurityVulnView; t: T }) {
  return v.fixedIn ? (
    <span className="crm-tnum">{t("monitoring.security.fixedIn", { version: v.fixedIn })}</span>
  ) : (
    <span className="crm-mut">{t("monitoring.security.noFix")}</span>
  );
}

/**
 * A whole software's fix: the version the server found that closes every
 * record with a fix, else "the latest version" when an update helps but no
 * single version is known (an older scan, or no patched version clears them
 * all), else that there is no fix. Under it, how many records no update can
 * close yet.
 */
export function GroupFix({ group, t }: { group: VulnGroup; t: T }) {
  if (!group.hasFix) return <span className="crm-mut">{t("monitoring.security.noFix")}</span>;
  return (
    <span className="grid gap-0.5">
      <span className="crm-tnum">
        {group.fixAll ? t("monitoring.security.fixAll", { version: group.fixAll }) : t("monitoring.security.fixLatest")}
      </span>
      {group.unfixed > 0 ? (
        <span className="crm-mut text-[11.5px]">{t("monitoring.security.unfixedSome", { n: group.unfixed })}</span>
      ) : null}
    </span>
  );
}
